import moment from 'moment';
import type { CSSProperties } from 'react';
import { useState } from 'react';

import { AGTable as AGTableRaw } from '../AGTable';

// biome-ignore lint/suspicious/noExplicitAny: AGTable is legacy .jsx with dynamic col-def shape
type AnyData = any;

// biome-ignore lint/suspicious/noExplicitAny: see above
const AGTable: any = AGTableRaw;

interface Job {
  _id: string;
  description?: string;
  status?: 'completed' | 'processing' | 'pending' | 'failed' | string;
  progress?: number;
  audit?: { createdDate?: string };
}

const Svg = ({ d, color }: { d: string; color: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d={d} />
  </svg>
);

const DoneIcon = () => <Svg color="green" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />;
const CloseIcon = () => (
  <Svg
    color="red"
    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
  />
);
const QuestionIcon = () => (
  <Svg
    color="#666"
    d="M11.07 12.85c.77-1.39 2.25-2.21 3.11-3.44.91-1.29.4-3.7-2.18-3.7-1.69 0-2.52 1.28-2.87 2.34L6.54 6.96C7.25 4.83 9.18 3 11.99 3c2.35 0 3.96 1.07 4.78 2.41.7 1.15 1.11 3.3.03 4.9-1.2 1.77-2.35 2.31-2.97 3.45-.25.46-.35.76-.35 2.24h-2.83c-.01-.78-.13-2.05.42-3.15zM14 20c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2z"
  />
);

const getIcon = (status?: string) => {
  switch (status) {
    case 'completed':
      return <DoneIcon />;
    case 'processing':
    case 'pending':
      return (
        <div
          style={{
            width: 15,
            height: 15,
            border: '2px solid #1976d2',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'bb-spin 1s linear infinite',
          }}
        />
      );
    case 'failed':
      return <CloseIcon />;
    default:
      return <QuestionIcon />;
  }
};

const SUBTEXT: CSSProperties = {
  color: 'grey',
  marginLeft: 8,
  fontStyle: 'italic',
  whiteSpace: 'nowrap',
};

const JobRow = ({ description, status, progress }: Job) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <div style={{ marginRight: 8 }}>{getIcon(status)}</div>
    <div>{description || 'No title'}</div>
    {status === 'pending' && <div style={SUBTEXT}>(Pending)</div>}
    {status === 'processing' && <div style={SUBTEXT}>({progress || 0}%)</div>}
  </div>
);

const columns: Array<AnyData> = [
  {
    headerName: 'Jobs (Last 7 days)',
    flex: 2,
    sortable: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    contextMenuItems: [],
    wrapText: true,
    autoHeight: true,
    cellRenderer: ({ data }: { data: Job }) => <JobRow {...data} />,
  },
  {
    headerName: '',
    flex: 1,
    sortable: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    contextMenuItems: [],
    wrapText: true,
    autoHeight: true,
    cellRenderer: ({ data }: { data: Job }) => (
      <div style={{ color: 'grey', marginLeft: 8, fontStyle: 'italic' }}>
        {moment(data.audit?.createdDate).fromNow()}
      </div>
    ),
  },
];

export interface AsyncJobsProps {
  jobs: Array<Job>;
}

const AsyncJobs = ({ jobs }: AsyncJobsProps) => {
  const [, setTableApi] = useState<AnyData>();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 8px 8px -8px rgba(0,0,0,0.1) inset',
        backgroundColor: 'var(--chakra-colors-bg-subtle)',
      }}
    >
      <style>{`@keyframes bb-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ height: 200, minWidth: 600 }}>
        <AGTable
          selectionModel="singleRow"
          getRowId={({ data }: { data: Job }) => data._id}
          headerHeight={30}
          rowData={jobs}
          colDefs={columns}
          isMoveable={false}
          rowHeight={30}
          onUpdate={(api: AnyData) => setTableApi(api)}
        />
      </div>
    </div>
  );
};

export default AsyncJobs;
